/** Development Mandate options (from knowledge-base/DEVELOPMENT MANDATE.docx). */

export type MandateOption = {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
};

export type MandateSection = {
  key: keyof DevelopmentMandateSelections;
  title: string;
  description: string;
  options: MandateOption[];
};

export type DevelopmentMandateSelections = {
  format: string[];
  target_audience: string[];
  genre: string[];
  budget_range: string[];
  strategic_fit: string[];
  cast_lead_configuration: string[];
  lead_gender_requirement: string[];
  cast_representation_requirement: string[];
  character_identity_lived_experience: string[];
  relationship_group_dynamic: string[];
  language_cultural_specificity: string[];
  dealbreakers: string[];
};

export const EMPTY_DEVELOPMENT_MANDATE: DevelopmentMandateSelections = {
  format: [],
  target_audience: [],
  genre: [],
  budget_range: [],
  strategic_fit: [],
  cast_lead_configuration: [],
  lead_gender_requirement: [],
  cast_representation_requirement: [],
  character_identity_lived_experience: [],
  relationship_group_dynamic: [],
  language_cultural_specificity: [],
  dealbreakers: [],
};

export const DEVELOPMENT_MANDATE_SECTIONS: MandateSection[] = [
  {
    key: 'format',
    title: 'Format',
    description: 'Choose the intended format.',
    options: [
      { value: 'feature_film', label: 'Feature Film' },
      { value: 'tv_serial', label: 'TV Serial', disabled: true },
      { value: 'tv_procedural', label: 'TV Procedural', disabled: true },
    ],
  },
  {
    key: 'target_audience',
    title: 'Target Audience',
    description: 'Who does this need to reach?',
    options: [
      { value: 'families_all_ages', label: 'Families / All-Ages' },
      { value: 'children', label: 'Children' },
      { value: 'young_adult_teen', label: 'Young Adult / Teen' },
      { value: 'adults_18_34', label: 'Adults 18–34' },
      { value: 'adults_25_45', label: 'Adults 25–45' },
      { value: 'adults_45_plus', label: 'Adults 45+' },
      { value: 'not_sure_open', label: 'Not Sure / Open' },
    ],
  },
  {
    key: 'genre',
    title: 'Genre',
    description: 'What kind of story are you actively looking for?',
    options: [
      { value: 'drama', label: 'Drama' },
      { value: 'comedy', label: 'Comedy' },
      { value: 'romantic_comedy', label: 'Romantic Comedy' },
      { value: 'thriller', label: 'Thriller' },
      { value: 'horror', label: 'Horror' },
      { value: 'action', label: 'Action' },
      { value: 'crime', label: 'Crime' },
      { value: 'mystery', label: 'Mystery' },
      { value: 'science_fiction', label: 'Science Fiction' },
      { value: 'fantasy', label: 'Fantasy' },
      { value: 'adventure', label: 'Adventure' },
      { value: 'family', label: 'Family' },
      { value: 'animation', label: 'Animation' },
      { value: 'historical_period', label: 'Historical / Period' },
      { value: 'biographical_true_story', label: 'Biographical / True Story' },
      { value: 'faith_based', label: 'Faith-Based' },
      { value: 'musical', label: 'Musical' },
      { value: 'documentary', label: 'Documentary' },
      { value: 'arthouse', label: 'Arthouse' },
      { value: 'no_genre_preference', label: 'No Genre Preference' },
    ],
  },
  {
    key: 'budget_range',
    title: 'Budget Range',
    description: 'What production level is realistic?',
    options: [
      { value: 'micro_budget', label: 'Micro-Budget', description: 'Under $250k' },
      { value: 'ultra_low_budget', label: 'Ultra-Low Budget', description: '$250k–$1M' },
      { value: 'low_budget', label: 'Low Budget', description: '$1M–$3M' },
      { value: 'modest_budget', label: 'Modest Budget', description: '$3M–$7M' },
      { value: 'mid_budget', label: 'Mid Budget', description: '$7M–$15M' },
      { value: 'elevated_premium_independent', label: 'Elevated / Premium Independent', description: '$15M–$30M' },
      { value: 'studio_streamer_level', label: 'Studio / Streamer Level', description: '$30M–$75M' },
      { value: 'tentpole_large_scale', label: 'Tentpole / Large-Scale', description: '$75M+' },
      { value: 'flexible_budget_agnostic', label: 'Flexible / Budget-Agnostic' },
      { value: 'not_sure', label: 'Not Sure' },
    ],
  },
  {
    key: 'strategic_fit',
    title: 'Strategic Fit',
    description: 'Why are you looking for this kind of project now?',
    options: [
      { value: 'fill_slate_gap', label: 'Fill a Slate Gap' },
      { value: 'reach_specific_audience', label: 'Reach a Specific Audience' },
      { value: 'match_buyer_distributor_demand', label: 'Match Buyer / Distributor Demand' },
      { value: 'build_genre_lane', label: 'Build a Genre Lane' },
      { value: 'create_commercial_upside', label: 'Create Commercial Upside' },
      { value: 'create_prestige_awards_potential', label: 'Create Prestige / Awards Potential' },
      { value: 'support_international_sales', label: 'Support International Sales' },
      { value: 'leverage_existing_talent_relationships', label: 'Leverage Existing Talent or Relationships' },
      { value: 'exploit_existing_ip_rights', label: 'Exploit Existing IP or Rights' },
      { value: 'fit_production_capabilities', label: 'Fit Production Capabilities' },
      { value: 'test_new_market_audience', label: 'Test a New Market or Audience' },
      { value: 'brand_mission_alignment', label: 'Brand / Mission Alignment' },
      { value: 'open_opportunistic', label: 'Open / Opportunistic' },
    ],
  },
  {
    key: 'cast_lead_configuration',
    title: 'Cast / Lead Configuration',
    description: 'What kind of cast structure is the mandate asking for?',
    options: [
      { value: 'single_lead', label: 'Single Lead' },
      { value: 'dual_lead_two_hander', label: 'Dual Lead / Two-Hander' },
      { value: 'ensemble_cast', label: 'Ensemble Cast' },
      { value: 'family_ensemble', label: 'Family Ensemble' },
      { value: 'friend_group_ensemble', label: 'Friend Group Ensemble' },
      { value: 'workplace_ensemble', label: 'Workplace Ensemble' },
      { value: 'multi_generational_cast', label: 'Multi-Generational Cast' },
      { value: 'intergenerational_cast', label: 'Intergenerational Cast' },
      { value: 'youth_led_cast', label: 'Youth-Led Cast' },
      { value: 'adult_led_cast', label: 'Adult-Led Cast' },
      { value: 'older_lead_45_plus', label: 'Older Lead / 45+ Lead' },
      { value: 'no_clear_preference', label: 'No Clear Preference' },
      { value: 'open_depends_on_script', label: 'Open / Depends on Script' },
    ],
  },
  {
    key: 'lead_gender_requirement',
    title: 'Lead Gender Requirement',
    description: 'Is there a specific lead gender requirement?',
    options: [
      { value: 'female_lead', label: 'Female Lead' },
      { value: 'male_lead', label: 'Male Lead' },
      { value: 'non_binary_lead', label: 'Non-Binary Lead' },
      { value: 'female_led_cast', label: 'Female-Led Cast' },
      { value: 'male_led_cast', label: 'Male-Led Cast' },
      { value: 'mixed_gender_leads', label: 'Mixed-Gender Leads' },
      { value: 'gender_balanced_ensemble', label: 'Gender-Balanced Ensemble' },
      { value: 'no_gender_preference', label: 'No Gender Preference' },
      { value: 'open_depends_on_script', label: 'Open / Depends on Script' },
    ],
  },
  {
    key: 'cast_representation_requirement',
    title: 'Cast Representation Requirement',
    description: 'Is there a specific representation mandate for the cast?',
    options: [
      { value: 'bipoc_lead', label: 'BIPOC Lead' },
      { value: 'black_lead', label: 'Black Lead' },
      { value: 'latine_hispanic_lead', label: 'Latine / Hispanic Lead' },
      { value: 'asian_lead', label: 'Asian Lead' },
      { value: 'south_asian_lead', label: 'South Asian Lead' },
      { value: 'middle_eastern_north_african_lead', label: 'Middle Eastern / North African Lead' },
      { value: 'indigenous_lead', label: 'Indigenous Lead' },
      { value: 'mixed_race_lead', label: 'Mixed-Race Lead' },
      { value: 'white_lead', label: 'White Lead' },
      { value: 'multicultural_ensemble', label: 'Multicultural Ensemble' },
      { value: 'diverse_ensemble', label: 'Diverse Ensemble' },
      { value: 'no_race_ethnicity_preference', label: 'No Race / Ethnicity Preference' },
      { value: 'open_depends_on_script', label: 'Open / Depends on Script' },
    ],
  },
  {
    key: 'character_identity_lived_experience',
    title: 'Character Identity / Lived Experience',
    description: 'Are you actively looking for stories centred on a specific identity or lived experience?',
    options: [
      { value: 'lgbtq_lead_or_story', label: 'LGBTQ+ Lead or Story' },
      { value: 'disability_representation', label: 'Disability Representation' },
      { value: 'neurodivergent_representation', label: 'Neurodivergent Representation' },
      { value: 'immigrant_diaspora_experience', label: 'Immigrant / Diaspora Experience' },
      { value: 'working_class_characters', label: 'Working-Class Characters' },
      { value: 'faith_or_religious_identity', label: 'Faith or Religious Identity' },
      { value: 'military_veteran_experience', label: 'Military / Veteran Experience' },
      { value: 'rural_small_town_identity', label: 'Rural / Small-Town Identity' },
      { value: 'urban_inner_city_identity', label: 'Urban / Inner-City Identity' },
      { value: 'underrepresented_community', label: 'Underrepresented Community' },
      { value: 'culturally_specific_story', label: 'Culturally Specific Story' },
      { value: 'no_specific_identity_requirement', label: 'No Specific Identity Requirement' },
      { value: 'open_depends_on_script', label: 'Open / Depends on Script' },
    ],
  },
  {
    key: 'relationship_group_dynamic',
    title: 'Relationship / Group Dynamic',
    description: 'What core character dynamic is preferred?',
    options: [
      { value: 'female_friendship', label: 'Female Friendship' },
      { value: 'male_friendship', label: 'Male Friendship' },
      { value: 'mixed_gender_friendship', label: 'Mixed-Gender Friendship' },
      { value: 'romantic_pairing', label: 'Romantic Pairing' },
      { value: 'family_relationship', label: 'Family Relationship' },
      { value: 'parent_child_relationship', label: 'Parent / Child Relationship' },
      { value: 'sibling_relationship', label: 'Sibling Relationship' },
      { value: 'mentor_protege', label: 'Mentor / Protégé' },
      { value: 'team_crew_unit', label: 'Team / Crew / Unit' },
      { value: 'rivalry', label: 'Rivalry' },
      { value: 'community_group', label: 'Community Group' },
      { value: 'found_family', label: 'Found Family' },
      { value: 'workplace_group', label: 'Workplace Group' },
      { value: 'no_specific_dynamic_preference', label: 'No Specific Dynamic Preference' },
      { value: 'open_depends_on_script', label: 'Open / Depends on Script' },
    ],
  },
  {
    key: 'language_cultural_specificity',
    title: 'Language / Cultural Specificity',
    description: 'Does the project need to meet a language or cultural mandate?',
    options: [
      { value: 'english_language', label: 'English-Language' },
      { value: 'non_english_language', label: 'Non-English-Language' },
      { value: 'bilingual', label: 'Bilingual' },
      { value: 'multilingual', label: 'Multilingual' },
      { value: 'culturally_specific', label: 'Culturally Specific' },
      { value: 'international_story', label: 'International Story' },
      { value: 'local_regional_story', label: 'Local / Regional Story' },
      { value: 'diaspora_story', label: 'Diaspora Story' },
      { value: 'cross_cultural_story', label: 'Cross-Cultural Story' },
      { value: 'global_audience_friendly', label: 'Global Audience Friendly' },
      { value: 'no_language_requirement', label: 'No Language Requirement' },
      { value: 'open_depends_on_script', label: 'Open / Depends on Script' },
    ],
  },
  {
    key: 'dealbreakers',
    title: 'Dealbreakers',
    description: 'What would make this script a bad fit?',
    options: [
      { value: 'budget_too_high', label: 'Budget Too High' },
      { value: 'heavy_vfx', label: 'Heavy VFX' },
      { value: 'period_setting', label: 'Period Setting' },
      { value: 'too_many_locations', label: 'Too Many Locations' },
      { value: 'large_cast', label: 'Large Cast' },
      { value: 'too_controversial', label: 'Too Controversial' },
      { value: 'unclear_rights_ip_issues', label: 'Unclear Rights / IP Issues' },
      { value: 'weak_commercial_hook', label: 'Weak Commercial Hook' },
      { value: 'too_niche', label: 'Too Niche' },
      { value: 'too_dark_bleak', label: 'Too Dark / Bleak' },
      { value: 'no_clear_lead_role', label: 'No Clear Lead Role' },
      { value: 'low_production_readiness', label: 'Low Production Readiness' },
      { value: 'complex_stunts_action', label: 'Complex Stunts / Action' },
      { value: 'children_minors_as_leads', label: 'Children / Minors as Leads' },
      { value: 'animals_required', label: 'Animals Required' },
      { value: 'music_licensing_heavy', label: 'Music Licensing Heavy' },
      { value: 'difficult_to_cast', label: 'Difficult to Cast' },
      { value: 'brand_mission_misalignment', label: 'Brand / Mission Misalignment' },
      { value: 'excessive_violence', label: 'Excessive Violence' },
      { value: 'sexual_content', label: 'Sexual Content' },
      { value: 'political_content', label: 'Political Content' },
      { value: 'religious_content', label: 'Religious Content' },
      { value: 'drug_use', label: 'Drug Use' },
      { value: 'ai_generated_scripts', label: 'AI Generated Scripts' },
      { value: 'not_sure_no_hard_dealbreakers', label: 'Not Sure / No Hard Dealbreakers' },
    ],
  },
];

export function normalizeMandate(
  raw: Partial<DevelopmentMandateSelections> | null | undefined,
): DevelopmentMandateSelections {
  const base = { ...EMPTY_DEVELOPMENT_MANDATE };
  if (!raw) return base;
  for (const section of DEVELOPMENT_MANDATE_SECTIONS) {
    const values = raw[section.key];
    base[section.key] = Array.isArray(values) ? [...values] : [];
  }
  return base;
}
